import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';

const TimetableNotificationCard = ({ userType, teacherId, studentClass }) => {
  const [currentClass, setCurrentClass] = useState(null);
  const [nextClass, setNextClass] = useState(null);
  const [todaysClasses, setTodaysClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    loadTimetableData();

    // Auto-refresh every 5 minutes
    const interval = setInterval(() => {
      loadTimetableData();
      setLastUpdate(new Date());
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [userType, teacherId, studentClass]);

  const loadTimetableData = async () => {
    try {
      let classParam = '';
      if (userType === 'student' && studentClass) {
        classParam = `?className=${encodeURIComponent(studentClass)}`;
      }

      const [currentRes, nextRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/schedules/current/${userType}/${teacherId || 'dummy'}${classParam}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/schedules/next/${userType}/${teacherId || 'dummy'}${classParam}`)
      ]);

      if (currentRes.ok) {
        const currentData = await currentRes.json();
        setCurrentClass(currentData.currentClass);
        setNextClass(currentData.nextClass);
      }

      if (nextRes.ok) {
        const nextData = await nextRes.json();
        if (!nextClass && nextData.nextClass) {
          setNextClass(nextData.nextClass);
        }
      }

      // Load today's classes for overview
      await loadTodaysClasses();

    } catch (error) {
      console.error('Error loading timetable data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTodaysClasses = async () => {
    try {
      let endpoint = '';
      if (userType === 'teacher' && teacherId) {
        endpoint = `${API_ENDPOINTS.BASE_URL}/api/schedules/teacher/${teacherId}`;
      } else if (userType === 'student' && studentClass) {
        endpoint = `${API_ENDPOINTS.BASE_URL}/api/schedules/class/${encodeURIComponent(studentClass)}`;
      }

      if (endpoint) {
        const response = await fetch(endpoint);
        if (response.ok) {
          const schedules = await response.json();
          const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
          const todaySchedules = schedules.filter(s => s.day.toLowerCase() === today);
          setTodaysClasses(todaySchedules.sort((a, b) => a.startTime.localeCompare(b.startTime)));
        }
      }
    } catch (error) {
      console.error('Error loading today\'s classes:', error);
    }
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      'assembly': 'School',
      'class': 'Books',
      'break': '☕',
      'meal': 'Food',
      'play': '⚽'
    };
    return icons[activityType] || '📅';
  };

  const getActivityColor = (activityType) => {
    const colors = {
      'assembly': 'border-purple-500 bg-purple-50',
      'class': 'border-blue-500 bg-blue-50',
      'break': 'border-green-500 bg-green-50',
      'meal': 'border-orange-500 bg-orange-50',
      'play': 'border-pink-500 bg-pink-50'
    };
    return colors[activityType] || 'border-gray-500 bg-gray-50';
  };

  const getTimeRemaining = (schedule) => {
    if (!schedule || !schedule.startTime) return null;

    const now = new Date();
    const [hours, minutes] = schedule.startTime.split(':').map(Number);

    const classDateTime = new Date();
    classDateTime.setHours(hours, minutes, 0, 0);

    const diffMs = classDateTime - now;
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 0) return null; // Class has passed
    if (diffMins === 0) return 'Starting now';
    if (diffMins < 60) return `${diffMins} min`;

    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    return `${diffHours}h ${remainingMins}m`;
  };

  const getStatusColor = (timeRemaining, activityType) => {
    if (!timeRemaining || timeRemaining === 'Starting now') return 'border-red-500 bg-red-50';
    if (timeRemaining.includes('min') && parseInt(timeRemaining) <= 30) return 'border-yellow-500 bg-yellow-50';
    return getActivityColor(activityType);
  };

  const getStatusText = (timeRemaining) => {
    if (!timeRemaining) return 'Ended';
    if (timeRemaining === 'Starting now') return 'Starting Now';
    if (timeRemaining.includes('min') && parseInt(timeRemaining) <= 30) return 'Soon';
    return 'Upcoming';
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-blue-100 hover:shadow-2xl transition duration-300">
      {/* Header - Always visible */}
      <div
        className="p-6 cursor-pointer flex items-center justify-between"
        onClick={toggleExpanded}
      >
        <div>
          <h2 className="text-xl font-semibold text-blue-800">Today's Schedule</h2>
          <p className="text-gray-600 text-sm">
            {currentClass ? `Current: ${currentClass.subject}` :
             nextClass ? `Next: ${nextClass.subject} (${getTimeRemaining(nextClass)})` :
             todaysClasses.length > 0 ? `${todaysClasses.length} classes scheduled` :
             'No classes today'}
          </p>
        </div>
        <div className="flex items-center">
          <span className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
      </div>

      {/* Collapsible Content */}
      <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-6 pb-6">
          <div className="space-y-4">
            {/* Current/Next Class Alert */}
            {(currentClass || nextClass) && (
              <div className={`rounded-lg shadow-md p-6 border-l-4 ${getStatusColor(getTimeRemaining(nextClass || currentClass), nextClass?.activityType || currentClass?.activityType)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {currentClass ? 'Current Class' : 'Next Class'}
                      </h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        currentClass
                        ? 'bg-green-100 text-green-800'
                          : getStatusText(getTimeRemaining(nextClass)) === 'Soon'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {currentClass ? 'In Session' : getStatusText(getTimeRemaining(nextClass))}
                      </span>
                    </div>

                    <div className="space-y-1">
                      <p className="text-xl font-bold text-gray-900">
                        {currentClass?.subject || nextClass?.subject}
                      </p>
                      <p className="text-gray-600">
                        {currentClass?.className || nextClass?.className} ? {currentClass?.startTime || nextClass?.startTime}-{currentClass?.endTime || nextClass?.endTime}
                      </p>
                      <p className="text-sm text-gray-500">
                        {currentClass?.room || nextClass?.room || 'Room TBD'}
                      </p>
                      {userType === 'teacher' && (
                        <p className="text-sm text-gray-500">
                          Teacher: {currentClass?.teacherName || nextClass?.teacherName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    {!currentClass && nextClass && (
                      <div className="text-2xl font-bold text-gray-800">
                        {getTimeRemaining(nextClass)}
                      </div>
                    )}
                    {currentClass && (
                      <div className="text-2xl font-bold text-green-600">
                        Live
                      </div>
                    )}
                  </div>
                </div>

                {userType === 'teacher' && currentClass && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
                      Mark Attendance
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Today's Overview */}
            {todaysClasses.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Today's Schedule</h3>
                  <span className="text-xs text-gray-500">
                    Updated: {lastUpdate.toLocaleTimeString()}
                  </span>
                </div>

                <div className="space-y-2">
                  {todaysClasses.slice(0, 4).map((cls, index) => (
                    <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                      <div className="flex-1">
                        <span className="font-medium text-gray-800">{cls.subject}</span>
                        <span className="text-gray-600 ml-2">{cls.startTime}-{cls.endTime}</span>
                        {userType === 'teacher' && (
                          <span className="text-gray-500 ml-2">({cls.className})</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {cls.room || 'TBD'}
                      </div>
                    </div>
                  ))}

                  {todaysClasses.length > 4 && (
                    <div className="text-center pt-2">
                      <span className="text-sm text-gray-500">
                        +{todaysClasses.length - 4} more classes today
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* No Classes Today */}
            {!currentClass && !nextClass && todaysClasses.length === 0 && (
              <div className="bg-gray-50 rounded-lg shadow-md p-6 border-l-4 border-gray-400">
                <div className="text-center">
                  <div className="text-4xl mb-2">📅</div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-1">No Classes Today</h3>
                  <p className="text-gray-500 text-sm">
                    {userType === 'teacher' ? 'You have no classes scheduled for today.' : 'No classes scheduled for your class today.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimetableNotificationCard;
